import { useEffect, useRef, useState } from 'preact/hooks';
import { api } from '../api.js';
import { t } from '../i18n/index.js';
import { Logo } from '../components/Logo.js';
import { Footer } from '../components/Footer.js';
import { GameIcon } from '../components/GameIcon.js';
import { BingoLobbyCard } from '../components/BingoLobbyCard.js';
import { PIECE_SVG } from '../games/chess/pieces.js';
import { LoginForm } from './LoginView.js';
import { JOIN_ROUTES } from '../../shared/registration.js';
import { ALL_GAME_TYPES, type AnyGameType, type MeUser } from '../../shared/protocol.js';

interface Summary {
  online: number;
  games: number;
  played: number;
}

/** Sections fade and rise into view when scrolled to (once). */
function useReveal() {
  useEffect(() => {
    const items = document.querySelectorAll<HTMLElement>('[data-reveal]');
    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('in'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          e.target.classList.add('in');
          observer.unobserve(e.target);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' },
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

/** A number that counts up to its value when it changes. */
function CountUp(props: { value: number }) {
  const [shown, setShown] = useState(0);
  const from = useRef(0);
  useEffect(() => {
    const start = performance.now();
    const begin = from.current;
    const target = props.value;
    let frame = 0;
    const step = (now: number) => {
      const p = Math.min(1, (now - start) / 1200);
      const eased = 1 - Math.pow(1 - p, 3);
      setShown(Math.round(begin + (target - begin) * eased));
      if (p < 1) frame = requestAnimationFrame(step);
      else from.current = target;
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [props.value]);
  return <>{shown.toLocaleString('fi-FI')}</>;
}

function Piece(props: { code: string }) {
  // Fixed markup from the chess piece set (pieces.ts), never user input.
  return <svg class="lp-piece" viewBox="0 0 45 45" aria-hidden="true" dangerouslySetInnerHTML={{ __html: PIECE_SVG[props.code] ?? '' }} />;
}

/** Animated game showcase in the hero: small living versions of the games. */
function Showcase() {
  // A calm middlegame; the white knight on g1 jumps to f3 and back (CSS animation).
  const chess: Record<number, string> = { 2: 'bk', 9: 'bp', 10: 'bp', 13: 'bp', 18: 'bn', 21: 'bb', 28: 'wp', 34: 'wb', 52: 'wp', 53: 'wp', 59: 'wq', 60: 'wk' };
  return (
    <div class="lp-showcase" aria-label={t.landing.showcase} role="img">
      <div class="lp-tile lp-chess">
        <div class="lp-chess-board">
          {Array.from({ length: 64 }, (_, i) => (
            <span class={`lp-sq ${(Math.floor(i / 8) + (i % 8)) % 2 ? 'd' : 'l'}`}>{chess[i] && <Piece code={chess[i]!} />}</span>
          ))}
          <span class="lp-chess-mover">
            <Piece code="wn" />
          </span>
        </div>
      </div>
      <div class="lp-tile lp-othello">
        {Array.from({ length: 16 }, (_, i) => (
          <span class="lp-o-cell">
            {[5, 6, 9, 10, 2, 13, 7].includes(i) && <span class={`lp-disc ${[5, 10, 13].includes(i) ? 'b' : 'w'} ${[6, 9, 2].includes(i) ? 'flip' : ''}`} style={{ animationDelay: `${(i % 4) * 0.35}s` }} />}
          </span>
        ))}
      </div>
      <div class="lp-tile lp-snake">
        <svg viewBox="0 0 120 80" aria-hidden="true">
          <defs>
            <pattern id="lp-grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M10 0H0V10" fill="none" stroke="rgba(61,220,151,0.12)" stroke-width="1" />
            </pattern>
          </defs>
          <rect width="120" height="80" fill="url(#lp-grid)" />
          <path class="lp-snake-body" d="M15 65 H55 V45 H95 V15 H35 V35 H15 V65" />
          <circle class="lp-apple" cx="95" cy="65" r="4" />
        </svg>
      </div>
      <div class="lp-tile lp-dice">
        <span class="lp-die d1">
          {[0, 1, 2, 3, 4].map((n) => (
            <i class={`pip p${n}`} />
          ))}
        </span>
        <span class="lp-die d2">
          {[0, 1, 2].map((n) => (
            <i class={`pip q${n}`} />
          ))}
        </span>
      </div>
      <div class="lp-tile lp-radar">
        <span class="lp-radar-sweep" />
        <span class="lp-blip b1" />
        <span class="lp-blip b2" />
      </div>
    </div>
  );
}

const GAME_BADGES: Record<AnyGameType, (keyof typeof t.landing.badges)[]> = {
  battleship: ['duel', 'computer'],
  othello: ['duel', 'computer'],
  memory: ['duel'],
  chess: ['duel', 'computer', 'clock'],
  snake: ['realtime', 'computer', 'solo'],
  tetrax: ['realtime', 'computer', 'solo'],
  dice: ['table', 'computer'],
  vale: ['table', 'computer', 'bluff'],
};

/**
 * Public front page (user request 2026-09-30): what Areena is, the games and how to join. The
 * login is one clear button in the top bar and the hero; the form opens in a dialog.
 */
export function LandingView(props: { onLogin: (user: MeUser) => void; notice?: string | null }) {
  const [loginOpen, setLoginOpen] = useState(false);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [codeRegistration, setCodeRegistration] = useState(false);
  const [radio, setRadio] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const L = t.landing;
  useReveal();

  useEffect(() => {
    const load = () =>
      api<Summary>('GET', '/api/public/summary')
        .then(setSummary)
        .catch(() => {});
    void load();
    const timer = setInterval(() => void load(), 30_000);
    api<{ codeRegistration: boolean }>('GET', '/api/join/status')
      .then((r) => setCodeRegistration(r.codeRegistration))
      .catch(() => {});
    api<{ radio: { name: string } }>('GET', '/api/radio/public')
      .then((r) => setRadio(r.radio.name))
      .catch(() => {});
    const onScroll = () => setScrolled(scrollY > 12);
    addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearInterval(timer);
      removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    if (!loginOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setLoginOpen(false);
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [loginOpen]);

  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const games: readonly AnyGameType[] = ALL_GAME_TYPES;

  return (
    <div class="landing">
      <div class="lp-aurora" aria-hidden="true">
        <span class="a1" />
        <span class="a2" />
        <span class="a3" />
      </div>

      <header class={`lp-nav ${scrolled ? 'scrolled' : ''}`}>
        <div class="lp-nav-inner">
          <button class="lp-brand" onClick={() => scrollTo('lp-top')} aria-label={t.appName}>
            <Logo />
          </button>
          <nav class="lp-links">
            <button onClick={() => scrollTo('lp-games')}>{L.navGames}</button>
            <button onClick={() => scrollTo('lp-features')}>{L.navFeatures}</button>
            <button onClick={() => scrollTo('lp-join')}>{L.navJoin}</button>
          </nav>
          <button class="btn btn-primary lp-login-btn" onClick={() => setLoginOpen(true)}>
            {L.login}
          </button>
        </div>
      </header>

      <main id="lp-top">
        {props.notice && (
          <p class="lp-notice" role="status">
            {props.notice}
          </p>
        )}
        <section class="lp-hero">
          <div class="lp-hero-text">
            <span class="lp-eyebrow">{L.eyebrow}</span>
            <h1 class="lp-title">
              {L.title1} <span class="lp-accent">{L.titleAccent}</span> {L.title2}
            </h1>
            <p class="lp-lead">{L.lead}</p>
            <div class="lp-cta">
              <button class="btn btn-primary lp-cta-main" onClick={() => setLoginOpen(true)}>
                {L.ctaLogin}
              </button>
              <a class="btn lp-cta-ghost" href={`#/${JOIN_ROUTES.guest}`}>
                {L.ctaGuest} →
              </a>
            </div>
            <div class="lp-stats" aria-live="polite">
              <div class="lp-stat">
                <strong>
                  <span class="lp-live-dot" aria-hidden="true" />
                  {summary ? <CountUp value={summary.online} /> : '–'}
                </strong>
                <span>{L.statOnline(summary?.online ?? 0)}</span>
              </div>
              <div class="lp-stat">
                <strong>{summary ? <CountUp value={summary.games} /> : '–'}</strong>
                <span>{L.statGames(summary?.games ?? 0)}</span>
              </div>
              <div class="lp-stat">
                <strong>{summary ? <CountUp value={summary.played} /> : '–'}</strong>
                <span>{L.statPlayed(summary?.played ?? 0)}</span>
              </div>
            </div>
          </div>
          <Showcase />
        </section>

        <div class="lp-section lp-bingo" data-reveal>
          <BingoLobbyCard />
        </div>

        <section class="lp-section" id="lp-games">
          <div class="lp-section-head" data-reveal>
            <h2>{L.gamesTitle}</h2>
            <p>{L.gamesLead}</p>
          </div>
          <div class="lp-games">
            {games.map((g, i) => (
              <article class="lp-game" data-reveal style={{ transitionDelay: `${(i % 3) * 80}ms` }}>
                <GameIcon game={g} size={52} />
                <h3>{t.games[g].name}</h3>
                <p>{t.games[g].description}</p>
                <div class="lp-badges">
                  {GAME_BADGES[g].map((b) => (
                    <span class={`lp-badge ${b}`}>{L.badges[b]}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section class="lp-section" id="lp-features">
          <div class="lp-section-head" data-reveal>
            <h2>{L.featuresTitle}</h2>
          </div>
          <div class="lp-features">
            {L.features.map((f, i) => (
              <article class="lp-feature" data-reveal style={{ transitionDelay: `${(i % 3) * 80}ms` }}>
                <span class="lp-feature-icon" aria-hidden="true">
                  {f.icon}
                </span>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </article>
            ))}
          </div>
          {radio && (
            <a class="lp-radio" href="/kuuntele/" target="_blank" rel="noopener" data-reveal>
              <span class="lp-eq" aria-hidden="true">
                <i />
                <i />
                <i />
                <i />
              </span>
              <span>
                <strong>{L.radioTitle(radio)}</strong>
                <span class="muted"> – {L.radioText}</span>
              </span>
              <span class="lp-radio-link">{L.radioLink} →</span>
            </a>
          )}
        </section>

        <section class="lp-section lp-join" id="lp-join">
          <div class="lp-section-head" data-reveal>
            <h2>{L.joinTitle}</h2>
            <p>{L.joinLead}</p>
          </div>
          <div class="lp-join-cards">
            {codeRegistration && (
              <a class="lp-join-card" href={`#/${JOIN_ROUTES.code}`} data-reveal>
                <span class="lp-join-icon" aria-hidden="true">
                  🎟️
                </span>
                <h3>{L.joinCards.register.title}</h3>
                <p>{L.joinCards.register.text}</p>
                <span class="lp-join-go">{L.ctaRegister} →</span>
              </a>
            )}
            <a class="lp-join-card" href={`#/${JOIN_ROUTES.guest}`} data-reveal>
              <span class="lp-join-icon" aria-hidden="true">
                👋
              </span>
              <h3>{L.joinCards.guest.title}</h3>
              <p>{L.joinCards.guest.text}</p>
              <span class="lp-join-go">{L.ctaGuest} →</span>
            </a>
            <button class="lp-join-card" onClick={() => setLoginOpen(true)} data-reveal>
              <span class="lp-join-icon" aria-hidden="true">
                🔑
              </span>
              <h3>{L.joinCards.login.title}</h3>
              <p>{L.joinCards.login.text}</p>
              <span class="lp-join-go">{L.ctaLogin} →</span>
            </button>
          </div>
        </section>
      </main>

      <Footer />

      {loginOpen && (
        <div class="overlay lp-login-overlay" role="presentation" onClick={() => setLoginOpen(false)}>
          <div class="lp-login-dialog" role="dialog" aria-modal="true" aria-labelledby="login-title" onClick={(e) => e.stopPropagation()}>
            <button class="lp-login-close" onClick={() => setLoginOpen(false)} aria-label={L.closeLogin}>
              ×
            </button>
            <LoginForm onLogin={props.onLogin} dialog />
          </div>
        </div>
      )}
    </div>
  );
}
